import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-or-later
import { cloneElement, forwardRef, isValidElement, } from 'react';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
/**
 * DropdownMenu primitive built on `@radix-ui/react-dropdown-menu` —
 * Radix handles roving tab-index, type-ahead, Escape close, and ARIA
 * roles/states. We layer the design-system styles + a few opinionated
 * conveniences (leading icon slot, destructive item variant).
 *
 * Composition:
 *   <DropdownMenu>
 *     <DropdownMenu.Trigger>•••</DropdownMenu.Trigger>
 *     <DropdownMenu.Content>
 *       <DropdownMenu.Item icon={<Eye/>} onSelect={open}>Open</DropdownMenu.Item>
 *       <DropdownMenu.Item icon={<Copy/>} onSelect={duplicate}>Duplicate</DropdownMenu.Item>
 *       <DropdownMenu.Separator />
 *       <DropdownMenu.Item destructive icon={<Trash2/>} onSelect={archive}>
 *         Archive
 *       </DropdownMenu.Item>
 *     </DropdownMenu.Content>
 *   </DropdownMenu>
 */
const DropdownMenuRoot = DropdownPrimitive.Root;
const DropdownMenuTrigger = DropdownPrimitive.Trigger;
const DropdownMenuPortal = DropdownPrimitive.Portal;
const DropdownMenuGroup = DropdownPrimitive.Group;
const DropdownMenuLabel = DropdownPrimitive.Label;
const DropdownMenuContent = forwardRef(function DropdownMenuContent(props, ref) {
    const { sideOffset = 4, ...rest } = props;
    return (_jsx(DropdownMenuPortal, { children: _jsx(DropdownPrimitive.Content, { ref: ref, sideOffset: sideOffset, "data-dropdown-menu-content": "true", ...rest }) }));
});
const DropdownMenuItem = forwardRef(function DropdownMenuItem(props, ref) {
    const { icon, destructive, children, asChild, ...rest } = props;
    const iconSlot = icon !== undefined ? (_jsx("span", { "data-dropdown-menu-item-icon": "true", "aria-hidden": "true", children: icon })) : null;
    // With `asChild`, Radix uses its Slot pattern to graft Item behavior onto
    // the consumer's element (e.g. a <Link>). Slot requires exactly one React
    // element child, so we must NOT pass our icon-span + label-span fragment
    // alongside it — that's what raises `React.Children.only`. Instead, clone
    // the consumer's element and inject the icon + their own children as the
    // visual content, then hand the cloned element to Slot as the single child.
    if (asChild === true && isValidElement(children)) {
        const child = children;
        return (_jsx(DropdownPrimitive.Item, { ref: ref, asChild: true, "data-dropdown-menu-item": "true", "data-destructive": destructive === true ? 'true' : undefined, ...rest, children: cloneElement(child, {}, _jsxs(_Fragment, { children: [iconSlot, _jsx("span", { "data-dropdown-menu-item-label": "true", children: child.props.children })] })) }));
    }
    return (_jsxs(DropdownPrimitive.Item, { ref: ref, "data-dropdown-menu-item": "true", "data-destructive": destructive === true ? 'true' : undefined, ...rest, children: [iconSlot, _jsx("span", { "data-dropdown-menu-item-label": "true", children: children })] }));
});
const DropdownMenuSeparator = forwardRef(function DropdownMenuSeparator(props, ref) {
    return _jsx(DropdownPrimitive.Separator, { ref: ref, "data-dropdown-menu-separator": "true", ...props });
});
const DropdownMenuFn = DropdownMenuRoot;
DropdownMenuFn.Trigger = DropdownMenuTrigger;
DropdownMenuFn.Content = DropdownMenuContent;
DropdownMenuFn.Item = DropdownMenuItem;
DropdownMenuFn.Separator = DropdownMenuSeparator;
DropdownMenuFn.Group = DropdownMenuGroup;
DropdownMenuFn.Label = DropdownMenuLabel;
export const DropdownMenu = DropdownMenuFn;
//# sourceMappingURL=DropdownMenu.js.map